/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchAccountMagicNfts,
  fetchNftInfo,
  fetchMagicNfts,
} from './fetchAccountNftInfo';
import { NFTMarketplaceState } from 'state/types';

const initialCollection = (localStorage.getItem('collection') ||
  'revelation') as NFTMarketplaceState['collection'];

const initialState: NFTMarketplaceState = {
  inventoryNfts: null,
  selectedNft: {},
  listedNfts: null,
  collection: initialCollection,
};

export const NFTMarketplaceSlice = createSlice({
  name: 'NFTMarketplace',
  initialState,
  reducers: {
    setData: (state, action) => {
      if (
        action.payload.inventoryNfts ||
        (action.payload.inventoryNfts == null &&
          action.payload.inventoryNfts !== undefined)
      ) {
        state.inventoryNfts = action.payload.inventoryNfts;
      }

      if (action.payload.selectedNft) {
        state.selectedNft = action.payload.selectedNft;
      }
      if (action.payload.listedNfts) {
        state.listedNfts = action.payload.listedNfts;
      }
      if (action.payload.collection) {
        state.collection = action.payload.collection;
      }
    },
  },
});

// Actions
export const { setData } = NFTMarketplaceSlice.actions;

export const fetchAccountMagicNftsAsync =
  (account: string, pageNumber?: number) => async (dispatch: any) => {
    const { inventoryNfts } = await fetchAccountMagicNfts(
      pageNumber ? pageNumber : 0,
      account,
    );

    dispatch(
      setData({
        inventoryNfts,
      }),
    );
  };

export const fetchMagicNftsAsync =
  (pageNumber: number, rarity?: string[], latestCall?: boolean) =>
  async (dispatch: any) => {
    const { listedNfts } = await fetchMagicNfts(pageNumber, rarity, latestCall);

    dispatch(
      setData({
        listedNfts: listedNfts,
      }),
    );
  };

export const setAccountMagicNftsEmptyAsync = () => async (dispatch: any) => {
  dispatch(
    setData({
      inventoryNfts: null,
    }),
  );
};

const AUCTION_KEYS = ['startingPrice', 'endingPrice', 'duration', 'startAt', 'isMCRT', 'seller', 'contractAddress'];
const DISPLAY_KEYS = ['name', 'image', 'description', 'attributes'];

function minimalNft(tokenId: string, contractAddress: string) {
  return {
    tokenID: tokenId,
    contractAddress: contractAddress || '',
    name: `NFT #${tokenId}`,
    description: '',
    image: '',
    attributes: [],
  };
}

function isEmpty(v: any, key: string): boolean {
  if (v == null) return true;
  if (key === 'startingPrice' || key === 'endingPrice') return Number(v) === 0;
  if (key === 'name' || key === 'image' || key === 'description') return String(v).trim() === '';
  if (key === 'attributes') return !Array.isArray(v) || v.length === 0;
  return false;
}

function isNextEmpty(next: any): boolean {
  const hasPrice = !isEmpty(next?.startingPrice, 'startingPrice') || !isEmpty(next?.endingPrice, 'endingPrice');
  const hasDisplay = !isEmpty(next?.image, 'image') || !isEmpty(next?.name, 'name');
  return !hasPrice && !hasDisplay;
}

export const fetchNftInfoAsync =
  (tokenId: string, contractAddress: string) => async (dispatch: any, getState: any) => {
    const prev = getState?.()?.nftMarketplace?.selectedNft;
    const { selectedNft: next } = await fetchNftInfo(tokenId, contractAddress);
    const prevSameToken = prev && String(prev?.tokenID) === String(tokenId);
    let merged: any;
    if (next && !isNextEmpty(next)) {
      merged = { ...next };
      if (prevSameToken && prev) {
        for (const k of AUCTION_KEYS) {
          if (!isEmpty(prev[k], k) && isEmpty(merged[k], k)) (merged as any)[k] = prev[k];
        }
        for (const k of DISPLAY_KEYS) {
          if (!isEmpty(prev[k], k) && isEmpty(merged[k], k)) (merged as any)[k] = prev[k];
        }
      }
    } else {
      merged = prevSameToken ? prev : minimalNft(tokenId, contractAddress);
    }
    dispatch(setData({ selectedNft: merged }));
  };

export const setCurrentCollection =
  (collection: string) => async (dispatch: any) => {
    localStorage.setItem('collection', collection);

    dispatch(
      setData({
        collection,
      }),
    );
    
    // Handle navigation for special collections
    if (collection === 'dogemint') {
      window.location.href = '/doge_mint';
    }
  };

export const getNftContractAddress = () => {
  const currentCollection = localStorage.getItem('collection');
  switch (currentCollection) {
    case 'genesis':
      return process.env.REACT_APP_GENESIS_CONTRACT_ADDRESS || '';
    case 'dogemint':
      return process.env.REACT_APP_DOGE_CONTRACT_ADDRESS || '';
    case 'revelation':
    default:
      return process.env.REACT_APP_REVELATION_CONTRACT_ADDRESS || '';
  }
};

export default NFTMarketplaceSlice.reducer;
